import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, DollarSign, Ticket, Heart, MessageSquare, Search, UserCheck, Undo2, Trash2, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { IMAGES } from "@/config/content";
import api from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (s) => (s ? new Date(s).toLocaleString() : "—");

const StatCard = ({ Icon, label, value }) => (
  <div className="border border-gold/25 bg-ink2 p-6">
    <Icon className="h-6 w-6 text-gold" />
    <div className="mt-4 font-serif text-4xl gold-text">{value}</div>
    <div className="mt-1 text-sm text-ivory2/70">{label}</div>
  </div>
);

const StatusPill = ({ s }) => {
  const map = { paid: "bg-ethgreen/20 text-ethgreen border-ethgreen/40", pending: "bg-ethyellow/15 text-ethyellow border-ethyellow/40" };
  return <span className={`rounded-sm border px-2 py-0.5 text-xs uppercase tracking-wide ${map[s] || "bg-ivory2/10 text-ivory2 border-ivory2/30"}`}>{s}</span>;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("tickets");
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const toggleCheckin = async (t) => {
    const next = !t.checked_in;
    setTxs((prev) => prev.map((x) => (x.session_id === t.session_id ? { ...x, checked_in: next } : x)));
    try {
      await api.patch(`/admin/transactions/${t.session_id}/checkin`, { checked_in: next });
    } catch {
      setTxs((prev) => prev.map((x) => (x.session_id === t.session_id ? { ...x, checked_in: !next } : x)));
    }
  };

  const deleteTicket = async (t) => {
    if (!window.confirm(`Remove ticket for ${t.donor_name || "this guest"}? This cannot be undone.`)) return;
    const prev = txs;
    setTxs((cur) => cur.filter((x) => x.session_id !== t.session_id));
    try {
      await api.delete(`/admin/transactions/${t.session_id}`);
    } catch {
      setTxs(prev);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/tickets/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "gala-guest-list.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("Could not export the guest list. Please try again.");
    }
  };

  useEffect(() => { if (user === false) nav("/admin/login"); }, [user, nav]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/admin/stats").then((r) => setStats(r.data)),
      api.get("/admin/transactions").then((r) => setTxs(r.data)),
      api.get("/admin/messages").then((r) => setMsgs(r.data)),
    ]).finally(() => setLoading(false));
  }, [user]);

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-ink"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const tickets = txs.filter((t) => t.kind === "ticket");
  const donations = txs.filter((t) => t.kind === "donation");

  const rows = tab === "tickets" ? tickets : [];
  const q = search.trim().toLowerCase();
  const filtered = rows.filter((t) => !q ||
    (t.donor_name || "").toLowerCase().includes(q) ||
    (t.donor_email || "").toLowerCase().includes(q) ||
    (t.donor_phone || "").includes(q));
  const checkedInCount = tickets.filter((t) => t.checked_in).length;

  const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-ivory2/70">{children}</th>;
  const Td = ({ children }) => <td className="px-4 py-3 text-ivory2 align-top">{children}</td>;

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-gold/20 bg-ink2">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="EHSIMFFA seal" className="h-10 w-10" />
            <div>
              <div className="font-serif text-xl text-ivory">Admin Dashboard</div>
              <div className="text-xs text-ivory2/60">{user.email}</div>
            </div>
          </div>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout"
            className="inline-flex items-center gap-2 rounded-sm border border-gold/40 px-4 py-2 text-sm text-gold hover:bg-gold/10">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard Icon={DollarSign} label="Gala revenue (paid)" value={fmt(stats?.ticket_total)} />
              <StatCard Icon={Ticket} label="Tickets sold" value={stats?.ticket_count || 0} />
              <StatCard Icon={UserCheck} label={`Checked in / ${tickets.length} orders`} value={checkedInCount} />
              <StatCard Icon={MessageSquare} label="Contact messages" value={stats?.message_count || 0} />
            </div>

            <div className="mt-10 flex gap-2 border-b border-gold/20">
              {[["tickets", `Gala Tickets (${tickets.length})`], ["messages", `Messages (${msgs.length})`]].map(([k, label]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                  className={`px-4 py-3 text-sm font-semibold transition-colors ${tab === k ? "text-gold border-b-2 border-gold" : "text-ivory2/70 hover:text-ivory"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {tab !== "messages" && (
                <div className="relative w-full sm:max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory2/50" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, email or phone…"
                    data-testid="admin-search"
                    className="w-full rounded-sm border border-gold/30 bg-ink2 py-2.5 pl-9 pr-3 text-ivory placeholder:text-ivory2/40 focus:border-gold"
                  />
                </div>
              )}
              {tab === "tickets" && (
                <div className="flex items-center gap-4">
                  <button onClick={exportCsv} data-testid="export-csv-btn"
                    className="inline-flex items-center gap-2 rounded-sm border border-gold/40 bg-gold/10 px-3 py-2 text-sm font-semibold text-gold hover:bg-gold/20">
                    <Download className="h-4 w-4" /> Export guest list (CSV)
                  </button>
                  <div className="flex items-center gap-2 text-sm" data-testid="checkin-counter">
                    <UserCheck className="h-5 w-5 text-ethgreen" />
                    <span className="text-ivory2">Checked in:</span>
                    <span className="font-serif text-2xl text-gold">{checkedInCount}</span>
                    <span className="text-ivory2/60">/ {tickets.length} orders</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 overflow-x-auto border border-gold/20">
              {tab === "messages" ? (
                <table className="w-full min-w-[720px]" data-testid="admin-messages-table">
                  <thead className="bg-ink2"><tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Message</Th></tr></thead>
                  <tbody className="divide-y divide-gold/10">
                    {msgs.length === 0 ? <tr><Td>No messages yet.</Td></tr> : msgs.map((m, i) => (
                      <tr key={i} className="hover:bg-ink2/60">
                        <Td>{date(m.created_at)}</Td><Td>{m.name}</Td><Td>{m.email}</Td><Td>{m.phone || "—"}</Td>
                        <Td><span className="block max-w-md whitespace-pre-wrap">{m.message}</span></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full min-w-[820px]" data-testid={`admin-${tab}-table`}>
                  <thead className="bg-ink2"><tr><Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th>{tab === "tickets" ? <Th>Qty</Th> : <Th>Frequency</Th>}<Th>Amount</Th><Th>Status</Th>{tab === "tickets" && <Th>Check-In / Manage</Th>}</tr></thead>
                  <tbody className="divide-y divide-gold/10">
                    {filtered.length === 0 ? <tr><Td>No {tab} found.</Td></tr> : filtered.map((t, i) => (
                      <tr key={i} className={`hover:bg-ink2/60 ${t.checked_in ? "bg-ethgreen/5" : ""}`} data-testid={`ticket-row-${i}`}>
                        <Td>{date(t.created_at)}</Td><Td>{t.donor_name}</Td><Td>{t.donor_email}</Td><Td>{t.donor_phone || "—"}</Td>
                        {tab === "tickets" ? <Td>{t.quantity}</Td> : <Td className="capitalize">{t.frequency}</Td>}
                        <Td className="text-ivory font-medium">{fmt(t.amount)}</Td>
                        <Td><StatusPill s={t.payment_status} /></Td>
                        {tab === "tickets" && (
                          <Td>
                            <div className="flex items-center gap-2">
                              {t.checked_in ? (
                                <button onClick={() => toggleCheckin(t)} data-testid={`checkin-btn-${i}`}
                                  className="inline-flex items-center gap-1.5 rounded-sm border border-ethgreen/40 bg-ethgreen/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ethgreen hover:bg-ethgreen/25">
                                  <UserCheck className="h-4 w-4" /> Checked In <Undo2 className="h-3.5 w-3.5 opacity-70" />
                                </button>
                              ) : (
                                <button onClick={() => toggleCheckin(t)} data-testid={`checkin-btn-${i}`}
                                  className="inline-flex items-center gap-1.5 rounded-sm bg-gold px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ink hover:bg-goldhi">
                                  <UserCheck className="h-4 w-4" /> Check In
                                </button>
                              )}
                              <button onClick={() => deleteTicket(t)} data-testid={`delete-btn-${i}`} title="Remove ticket"
                                className="inline-flex items-center justify-center rounded-sm border border-red-500/40 bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </Td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
